import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
} from "@mui/material";

import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import QRCode from "react-qr-code";
import { useAwaitDialog } from "app/components/status_dialog";
import { Html5Qrcode } from "html5-qrcode";
import MDInput from "theme/components/MDInput";

const formatNumber = (value) => Number(value || 0).toLocaleString();

export default function InventoryTrackerPage() {
  const { ref } = useParams();
  const navigate = useNavigate();

  usePageTitle("Inventory");

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [resultData, setResultData] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [summary, setSummary] = useState({});
  const [role, setRole] = useState("AGENT");

  const [products, setProducts] = useState([]);
  const [recent, setRecent] = useState([]);

  const [openReceiveQR, setOpenReceiveQR] = useState(false);
  const [openReturnQR, setOpenReturnQR] = useState(false);

  const scannerRef = useRef(null);
  const [openScanner, setOpenScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState(null); // "TRANSFER" | "RECALL"
  const [openStockDialog, setOpenStockDialog] = useState(false);
  const [scannedAgent, setScannedAgent] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [qrCodeJson, setQrCodeJson] = useState(null);
  const scannerBusyRef = useRef(false);

  const handleClose = () => setOpen(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/agents/inventories/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setResultData(result.data);
            setTracker(result.data.data.tracker);
            setSummary(result.data.data.summary || {});
            setRole(result.data.data.role || "AGENT");
            setProducts(result.data.data.products || []);
            setRecent(result.data.data.recent || []);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  useEffect(() => {
    if (!openScanner) {
      return;
    }

    const timer = setTimeout(async () => {
      const element = document.getElementById("inventory-scanner");

      if (!element) return;

      const html5QrCode = new Html5Qrcode("inventory-scanner");
      scannerRef.current = html5QrCode;

      try {
        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          async (decodedText) => {
            if (scannerBusyRef.current) return;

            scannerBusyRef.current = true;

            try {
              const qr = JSON.parse(decodedText);

              await html5QrCode.stop().catch(() => {});
              scannerRef.current = null;

              setOpenScanner(false);

              setScannedAgent(qr);
              setSelectedProduct("");
              setQuantity("");
              setOpenStockDialog(true);
            } catch (e) {
              setSeverity("error");
              setErrMsg("Invalid QR code.");
              setOpen(true);
            } finally {
              scannerBusyRef.current = false;
            }
          },
          () => {},
        );
      } catch (e) {
        setSeverity("error");
        setErrMsg("Unable to access camera.");
        setOpen(true);
        setOpenScanner(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);

      const scanner = scannerRef.current;

      if (scanner && !scannerBusyRef.current) {
        scannerBusyRef.current = true;

        scanner
          .stop()
          .catch(() => {})
          .finally(() => {
            scannerRef.current = null;
            scannerBusyRef.current = false;
          });
      }
    };
  }, [openScanner]);

  async function submitStockAction() {
    if (!selectedProduct || !quantity) {
      setSeverity("error");
      setErrMsg("Please select a product and enter quantity.");
      setOpen(true);
      return;
    }

    const payload = {
      ...scannedAgent,
      product: selectedProduct,
      quantity: Number(quantity),
    };

    setOpenStockDialog(false);

    if (scannerMode === "TRANSFER") {
      await transferStock(payload);
    } else {
      await recallStock(payload);
    }
  }

  async function transferStock(qrString) {
    if (qrString.a === null) {
      setSeverity("error");
      setErrMsg("Invalid QR code.");
      setOpen(true);
      return;
    }

    if (qrString.r !== null) {
      if (qrString.r !== ref) {
        setSeverity("error");
        setErrMsg("Invalid inventory match");
        setOpen(true);
        return;
      }
    }

    if (qrString.t === null || qrString.t !== "I") {
      setSeverity("error");
      setErrMsg("Invalid QR code.");
      setOpen(true);
      return;
    }

    setShowLoading(true);
    const json = {
      reference: ref,
      agent: qrString.a,
      product: qrString.product,
      quantity: qrString.quantity,
    };

    await axios
      .post("agents/inventories/transfer.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(
            result.data.data.message,
            "success",
            "Success!",
          );

          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setOpen(true);
        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
          "success",
          "Success!",
        );
      });
  }

  async function recallStock(qrString) {
    if (qrString.a === null) {
      setSeverity("error");
      setErrMsg("Invalid QR code.");
      setOpen(true);
      return;
    }

    if (qrString.r !== null) {
      if (qrString.r !== ref) {
        setSeverity("error");
        setErrMsg("Invalid inventory match");
        setOpen(true);
        return;
      }
    }

    if (qrString.t === null || qrString.t !== "R") {
      setSeverity("error");
      setErrMsg("Invalid QR code.");
      setOpen(true);
      return;
    }

    setShowLoading(true);

    const json = {
      reference: ref,
      agent: qrString.a,
      product: qrString.product,
      quantity: qrString.quantity,
    };

    await axios
      .post("agents/inventories/recall.json", json)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(
            result.data.data.message,
            "success",
            "Success!",
          );

          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!",
        );
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openStockDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>
            {scannerMode === "TRANSFER" ? "Issue Stock" : "Recall Stock"}
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox pt={1} display="flex" flexDirection="column" gap={3}>
            <MDBox mt={1}>
              <MDInput
                select
                fullWidth
                label="Product"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                SelectProps={{
                  style: { padding: "12px 0px" },
                }}
              >
                {products.map((p) => (
                  <MenuItem key={p.reference} value={p.reference}>
                    {p.name} ({p.available})
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>

            <MDBox mt={1}>
              <MDInput
                label="Quantity"
                type="number"
                min="1"
                fullWidth
                variant="outlined"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            color="dark"
            variant="outlined"
            onClick={() => setOpenStockDialog(false)}
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={submitStockAction}
          >
            {scannerMode === "TRANSFER" ? "Issue" : "Recall"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openScanner} fullWidth maxWidth="sm">
        <DialogTitle>
          <MDBox>
            {scannerMode === "TRANSFER" ? "Scan Agent QR" : "Scan Return QR"}
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox pt={1}>
            <MDBox
              id="inventory-scanner"
              width="100%"
              minHeight={320}
              borderRadius="lg"
              overflow="hidden"
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            color="dark"
            variant="outlined"
            onClick={() => setOpenScanner(false)}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>

      {role !== "SUPERVISOR" && (
        <>
          <Dialog open={openReceiveQR} fullWidth maxWidth="xs">
            <DialogTitle>
              <MDBox>Receive Stock</MDBox>
            </DialogTitle>

            <DialogContent>
              <MDBox
                py={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <MDBox
                  p={2}
                  borderRadius="lg"
                  bgcolor="white"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={240}
                  height={240}
                >
                  <QRCode
                    value={qrCodeJson}
                    size={220}
                    style={{
                      height: "auto",
                      maxWidth: "100%",
                      width: "100%",
                    }}
                  />
                </MDBox>

                <MDBox mt={3} textAlign="center">
                  <MDTypography variant="h6" fontWeight="medium">
                    Ask your supervisor to scan this code
                  </MDTypography>

                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="light"
                  >
                    They will select products and quantities to transfer to you.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </DialogContent>

            <DialogActions>
              <MDButton
                onClick={() => setOpenReceiveQR(false)}
                color="dark"
                variant="outlined"
              >
                Close
              </MDButton>
            </DialogActions>
          </Dialog>

          <Dialog open={openReturnQR} fullWidth maxWidth="xs">
            <DialogTitle>
              <MDBox>Return Stock</MDBox>
            </DialogTitle>

            <DialogContent>
              <MDBox
                py={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <MDBox
                  p={2}
                  borderRadius="lg"
                  bgcolor="white"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={240}
                  height={240}
                >
                  <QRCode
                    value={qrCodeJson}
                    size={220}
                    style={{
                      height: "auto",
                      maxWidth: "100%",
                      width: "100%",
                    }}
                  />
                </MDBox>

                <MDBox mt={3} textAlign="center">
                  <MDTypography variant="h6" fontWeight="medium">
                    Ask your supervisor to scan this code
                  </MDTypography>

                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="light"
                  >
                    They will select products and quantities to recall from you.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </DialogContent>

            <DialogActions>
              <MDButton
                onClick={() => setOpenReturnQR(false)}
                color="dark"
                variant="outlined"
              >
                Close
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      {pageLoaded && tracker && (
        <MDBox mt={1} mb={5}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            pr={2}
          >
            <MDButton
              variant="text"
              color="dark"
              startIcon={<Icon>arrow_back</Icon>}
              onClick={() => navigate(-1)}
            >
              Back
            </MDButton>

            <Chip
              label={role === "SUPERVISOR" ? "Supervisor" : "Agent"}
              color={role === "SUPERVISOR" ? "info" : "default"}
              size="small"
            />
          </MDBox>

          <Card sx={{ marginTop: 0 }}>
            <MDBox p={3}>
              <MDTypography variant="h4" fontWeight="medium">
                {tracker.title}
              </MDTypography>

              <MDTypography variant="button" color="text" fontWeight="light">
                {tracker.account?.title}
              </MDTypography>

              {/* <Grid container spacing={2} mt={1}>
                <Grid item xs={4}>
                  <MDBox
                    p={2}
                    borderRadius="lg"
                    bgcolor="grey.100"
                    textAlign="center"
                  >
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="light"
                    >
                      Available
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="medium">
                      {formatNumber(summary.available)}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={4}>
                  <MDBox
                    p={2}
                    borderRadius="lg"
                    bgcolor="grey.100"
                    textAlign="center"
                  >
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="light"
                    >
                      Locked
                    </MDTypography>
                    <MDTypography
                      variant="h5"
                      fontWeight="medium"
                      color="warning.main"
                    >
                      {formatNumber(summary.locked)}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={4}>
                  <MDBox
                    p={2}
                    borderRadius="lg"
                    bgcolor="grey.100"
                    textAlign="center"
                  >
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="light"
                    >
                      Consumed
                    </MDTypography>
                    <MDTypography
                      variant="h5"
                      fontWeight="medium"
                      color="success.main"
                    >
                      {formatNumber(summary.consumed)}
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid> */}
            </MDBox>
          </Card>

          <MDBox mt={2}>
            {role === "SUPERVISOR" ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDButton
                    fullWidth
                    color="dark"
                    variant="contained"
                    startIcon={<Icon>qr_code_scanner</Icon>}
                    onClick={() => {
                      setScannerMode("TRANSFER");
                      setOpenScanner(true);
                    }}
                  >
                    Issue
                  </MDButton>
                </Grid>
                <Grid item xs={6}>
                  <MDButton
                    fullWidth
                    color="dark"
                    variant="outlined"
                    startIcon={<Icon>qr_code_scanner</Icon>}
                    onClick={() => {
                      setScannerMode("RECALL");
                      setOpenScanner(true);
                    }}
                  >
                    Recall
                  </MDButton>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDButton
                    fullWidth
                    color="dark"
                    variant="contained"
                    startIcon={<Icon>qr_code_2</Icon>}
                    onClick={() => {
                      const qrPayload = JSON.stringify({
                        r: ref,
                        a: resultData?.data?.reference,
                        t: "I",
                      });
                      setQrCodeJson(qrPayload);
                      setOpenReceiveQR(true);
                    }}
                  >
                    Receive
                  </MDButton>
                </Grid>
                <Grid item xs={6}>
                  <MDButton
                    fullWidth
                    color="dark"
                    variant="outlined"
                    startIcon={<Icon>qr_code_2</Icon>}
                    onClick={() => {
                      const qrPayload = JSON.stringify({
                        r: ref,
                        a: resultData?.data?.reference,
                        t: "R",
                      });
                      setQrCodeJson(qrPayload);
                      setOpenReturnQR(true);
                    }}
                  >
                    Return
                  </MDButton>
                </Grid>
              </Grid>
            )}
          </MDBox>

          <MDBox mt={5}>
            {/* <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Products
            </MDTypography> */}

            <MDBox display="flex" flexDirection="column" gap={2}>
              {products.map((row) => (
                <Card key={row.reference} sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="button" fontWeight="medium">
                        {row.name}
                      </MDTypography>

                      <MDTypography variant="h6" fontWeight="medium">
                        {formatNumber(row.available)}
                      </MDTypography>
                    </MDBox>

                    <MDBox mt={1} display="flex" gap={2}>
                      <MDTypography variant="caption" color="warning.main">
                        Locked: {formatNumber(row.locked)}
                      </MDTypography>

                      <MDTypography variant="caption" color="success.main">
                        Consumed: {formatNumber(row.consumed)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              ))}
            </MDBox>
          </MDBox>

          <MDBox mt={5}>
            <MDBox display="flex" justifyContent="space-between" px={2}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                History
              </MDTypography>
              <Icon>list</Icon>
            </MDBox>

            <MDBox display="flex" flexDirection="column" gap={2}>
              {recent.map((row, index) => (
                <Card key={index} sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="button" fontWeight="medium">
                        {row.product}
                      </MDTypography>

                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="medium"
                      >
                        {formatNumber(row.quantity)}
                      </MDTypography>
                    </MDBox>

                    <MDBox
                      mt={1}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="caption" color="text">
                        {row.entry_type}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        {row.created}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              ))}
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
